import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { RootState } from '../../app/store';
import { connect, ConnectedProps } from 'react-redux';
import { useIntl } from 'react-intl';
import Button from '../../components/Button/Button.component';
import FormField from '../../components/FormField/FormField.component';
import DialogWrap from '../../components/DiaogWrap/DiaogWrap.component';
import { StyledTable, Wrap } from '../../assets/styledAssets/grid';
import { PlusIcon, SearchIcon } from '../../components/Icons/Icons.component';
import { GridColumn } from '@progress/kendo-react-grid';
import {
  accentColorSecond,
  fontColorPrim,
  fontColorSecond, green, red,
  white,
} from '../../assets/styledAssets/vars';
import Moment from 'react-moment';
import messages from './messages';
import { deleteSpace, fetchSpaces, updateSpace } from './Spaces.actions';
import {
  setSearch,
  addSpaces,
  setActiveSpace,
  setShowSpaceDetails,
} from './Spaces.slice';
import { ActivityStatus, Gender } from '../../domains/app';
import debounce from 'lodash/debounce';
import AddEditSpace from './components/AddEditSpace/AddEditSpace.component';
import { Space } from './spaces';
import { createSpace } from '../../service/Space.service';

const connector = connect(
  (state: RootState) => ({
    listSpaces: state.spaces.listSpaces,
    search: state.spaces.search,
    isFetching: state.spaces.isFetching,
    total: state.spaces.total,
    pageSize: state.spaces.pageSize,
    user: state.app.user,
    activeSpace: state.spaces.activeSpace,
    showSpaceDetails: state.spaces.showSpaceDetails,
  }),
  {
    fetchSpaces,
    setSearch,
    addSpaces,
    setActiveSpace,
    updateSpace,
    setShowSpaceDetails,
    deleteSpace,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Spaces: FunctionComponent<PropsFromRedux> = ({
  listSpaces,
  fetchSpaces,
  search,
  setSearch,
  user,
  addSpaces,
  isFetching,
  total,
  pageSize,
  setActiveSpace,
  activeSpace,
  updateSpace,
  showSpaceDetails,
  setShowSpaceDetails,
  deleteSpace,
}) => {
  const intl = useIntl();

  const [skip, setSkip] = useState<number>(0);

  useEffect(() => {
    fetchSpaces(true);
    // eslint-disable-next-line
  }, []);

  const fetch = useMemo(
    () =>
      debounce(() => {
        fetchSpaces(true);
      }, 200),
    // eslint-disable-next-line
    [],
  );

  const handleChangeInput = (e: any) => {
    setSearch(e.target.value);
    fetch();
  };

  const handleAddEditSpace = useCallback(
    (s: Partial<Space>) => {
      if (user) {
        if (s.id) {
          updateSpace(s);
        } else {
          const space: Space = {
            name: s.name as string,
            ownerId: user.id as string,
          };
          createSpace(space).then((res) => {
            addSpaces([res]);
            setShowSpaceDetails(false);
          });
        }
      }
    },
    // eslint-disable-next-line
    [user],
  );

  const pageHandler = useCallback(
    (event: any) => {
      setSkip(event.page.skip);
      if (
        skip + pageSize + 3 >= listSpaces.length &&
        !isFetching &&
        total > listSpaces.length
      ) {
        fetchSpaces(false);
      }
    },
    [
      isFetching,
      listSpaces.length,
      total,
      fetchSpaces,
      setSkip,
      skip,
      pageSize,
    ],
  );

  return (
    <Wrap flexDir={'column'} overflow={['auto', 'unset']} flexWrap={'nowrap'}>
      <Wrap flexDir={'column'} width={'100%'}>
        <Wrap alignItems={['flex-start', 'center']} justifyCont={'space-between'} width={'100%'}>
          <Wrap alignItems={['flex-start', 'flex-end']} flexDir={['column', 'row']}>
            <Wrap
              fs={'3.2rem'}
              fw={'bold'}
              color={'#000'}
              alignItems={'flex-end'}
              borders={[[{ side: 'right', color: 'transparent' }], [{side: 'right', color: accentColorSecond}]]}
              padding={['0', '0 3rem 0 0']}
              margin={['0 0 3rem 0', '0 3rem 0 0']}
            >
              {intl.formatMessage(messages.title)}
            </Wrap>
            <Wrap
              fs={'1.8rem'}
              color={fontColorSecond}
              alignItems={'flex-end'}
              padding={'0 0 .5rem 0'}
            >
              {total} {intl.formatMessage(messages.totalSpaces)}
            </Wrap>
          </Wrap>
          <Wrap justifyCont={'flex-end'}>
            <Button primary onClick={() => setShowSpaceDetails(true)}>
              <PlusIcon color={white} width={'1.4rem'} />
              {intl.formatMessage(messages.addSpace)}
            </Button>
          </Wrap>
        </Wrap>
        <Wrap alignItems={'center'} margin={'4rem 0 3rem 0'} maxWidth={'34rem'} width={'100%'}>
          <FormField
            placeholder={'Search'}
            value={search}
            onChange={handleChangeInput}
            postfix={<SearchIcon color={fontColorPrim} width={'2rem'} />}
          />
        </Wrap>
      </Wrap>
      <Wrap
        grow={'1'}
        overflow={'hidden'}
        width={'100%'}
        height={'100%'}
        hiddenXS
      >
        <StyledTable
          style={{ height: '100%' }}
          data={listSpaces.slice(skip, skip + pageSize)}
          onRowClick={(e) => {
            setActiveSpace(e.dataItem);
            setShowSpaceDetails(true);
          }}
          onPageChange={pageHandler}
          scrollable={'virtual'}
          skip={skip}
          pageSize={pageSize}
          total={total}
          rowHeight={55}
        >
          <GridColumn
            field="name"
            title={intl.formatMessage(messages.space)}
            width={'150px'}
          />
          <GridColumn
            field="owner.firstName"
            title={intl.formatMessage(messages.fName)}
            width={'150px'}
          />
          <GridColumn
            field="owner.lastName"
            title={intl.formatMessage(messages.lName)}
            width={'150px'}
          />
          <GridColumn
            field="owner.gender"
            title={intl.formatMessage(messages.sex)}
            width={'90px'}
            cell={(props) => (
              <td>
                {props.dataItem.owner?.gender === Gender.Male
                  ? intl.formatMessage(messages.male)
                  : intl.formatMessage(messages.female)}
              </td>
            )}
          />
          <GridColumn
            field="birth"
            title={intl.formatMessage(messages.birth)}
            width={'150px'}
            cell={(props) => (
              <td>
                <Moment format={'DD MMM YYYY'}>
                  {props.dataItem.owner?.birthDate}
                </Moment>
              </td>
            )}
          />
          <GridColumn
            field="owner.email"
            title={intl.formatMessage(messages.email)}
          />
          <GridColumn
            field="owner.phone"
            title={intl.formatMessage(messages.phNumber)}
            width={'130px'}
          />
          <GridColumn
            field="owner.enrolled"
            title={intl.formatMessage(messages.enrolled)}
            cell={(props) => (
              <td>
                <Moment format={'DD MMM YYYY'}>
                  {props.dataItem.enrolled}
                </Moment>
              </td>
            )}
          />
          <GridColumn
            field="owner.status"
            title={intl.formatMessage(messages.status)}
            width={'90px'}
            cell={(props) => (
              <td>
                {props.dataItem.owner?.status === ActivityStatus.Active
                  ? <Wrap fw={'bold'} color={green}>{intl.formatMessage(messages.active)}</Wrap>
                  : <Wrap fw={'bold'} color={red}>{intl.formatMessage(messages.inactive)}</Wrap>
                }
              </td>
            )}
          />
        </StyledTable>
      </Wrap>

      <Wrap hiddenSM overflow={'auto'} width={'100%'}>
        {listSpaces.map((space : any, index)=>
          <Wrap onClick={() => {setActiveSpace(space);setShowSpaceDetails(true);}} flexDir={'column'} flexWrap={'wrap'} margin={'0 0 2rem 0'} width={'100%'} padding={'3rem'} br={'1.2rem'} bgColor={white} key={`card-${index}`}>
            <Wrap alignItems={'center'} justifyCont={'space-between'} width={'100%'} margin={'0 0 1rem 0'}>
              <Wrap fs={'2rem'} fw={'bold'}>{space.name}</Wrap>
              {space.owner?.status === ActivityStatus.Active
                ? <Wrap margin={'.2rem'} color={white} fs={'1.4rem'} padding={'.3rem .5rem'} br={'.5rem'} bgColor={green}>{intl.formatMessage(messages.active)}</Wrap>
                : <Wrap margin={'.2rem'} color={white} fs={'1.4rem'} padding={'.3rem .5rem'} br={'.5rem'} bgColor={red}>{intl.formatMessage(messages.inactive)}</Wrap>
              }
            </Wrap>
            <Wrap margin={'0 0 1rem 0'} fs={'1.8rem'} fw={'bold'}>{space.owner?.firstName} {space.owner?.lastName}</Wrap>
            <Wrap alignItems={'center'} margin={'0 0 2rem 0'} width={'100%'}>
              <Wrap fs={'1.5rem'} color={fontColorSecond} margin={'0 1rem 0 0'} padding={'0 1rem 0 0'} borders={[{side: 'right'}]}>
                <Wrap fs={'1.5rem'} color={fontColorSecond} margin={'0 .6rem 0 0'}>
                  {space.owner?.gender === Gender.Male
                    ? intl.formatMessage(messages.male)
                    : intl.formatMessage(messages.female)}</Wrap> <Moment format={'MM/DD/YY'}>{space.owner?.birthDate}</Moment>
              </Wrap>
              <Wrap justifyCont={'flex-end'}>
                <Wrap fs={'1.5rem'} color={fontColorSecond} margin={'0 .6rem 0 0'}>{intl.formatMessage(messages.enrolled)}</Wrap>
                <Wrap fs={'1.5rem'} color={fontColorSecond}><Moment format={'MM/DD/YY'}>{space.enrolled}</Moment></Wrap>
              </Wrap>
            </Wrap>
            <Wrap alignItems={'center'} margin={'0 0 1rem 0'}>
              <Wrap color={fontColorSecond}>{space.owner?.email}</Wrap>
            </Wrap>
            <Wrap alignItems={'center'}>
              <Wrap color={fontColorSecond}>{space.owner?.phone}</Wrap>
            </Wrap>
          </Wrap>)}
      </Wrap>

      {showSpaceDetails && (
        <DialogWrap
          title={
            activeSpace?.id
              ? intl.formatMessage(messages.spaceDetails)
              : intl.formatMessage(messages.addSpace)
          }
          children={
            <AddEditSpace
              space={activeSpace}
              onCancel={() => {
                setActiveSpace();
                setShowSpaceDetails(false);
              }}
              onSave={handleAddEditSpace}
              onDelete={deleteSpace}
            />
          }
        />
      )}
    </Wrap>
  );
};

export default connector(Spaces);
